import {
    formBuilderLoad,
    formBuilderLoaded,
    formStartNew,
    formUpdateSections,
    formRenameMultipleField,
    formUpdated,
    formChangeSection,
    formReorderSections,
    formUpdateEntries,
    formUpdateMultipleEntries,
    formSave,
    formUpdateName,
    formSetEntries,
    formSaved,
    formSaveAndRedirect,
    formDelete,
    formsLoadExisting,
    formDiscardChanges,
    formUpdateVisibilityAndValidation,
} from '../actions';
import { apiGet, apiPost, apiDelete } from '../api';
import { createLogic } from 'redux-logic';
import { push } from 'connected-react-router';
import {
    updateVisibilityAndValidationOnNameChange,
    updateVisibilityAndValidationOnDelete,
    stepBackActiveSection,
} from '../FormBuilder/formBuilderLogic';
const loadFormBuilder = createLogic({
    type: formBuilderLoad,
    latest: true,
    async process({ getState, action }, dispatch, done) {
        const formId = action.payload;
        const authContext = getState().authentication;
        const token = await authContext.getAccessToken(authContext.authorizationScopes.forms);

        apiGet(`/forms/${formId}`, token)
            .then((result) => {
                const form = result.data;
                // Use stepBackActiveSection to find last editable section
                const activeSection = stepBackActiveSection(form.sections, form.sections.length);
                dispatch(formBuilderLoaded({ form, activeSection, lastSavedForm: form }));
            })
            .catch(() => {})
            .finally(done);
    },
});

const startFormBuilder = createLogic({
    type: formStartNew,
    latest: true,
    warnTimeout: 0,
    process({ action }, dispatch) {
        const name = action.payload.name;
        const callBack = action.payload.callBack;

        let form;
        if (action.payload.formDefinition) {
            //omits the following propreties from formDefinition and places the remaining properties in updatedFormDefinition
            const { id, tableData, attachments, ...updatedFormDefinition } = action.payload.formDefinition;
            form = {
                ...updatedFormDefinition,
                name: name,
            };
            //omits template when copying form that contains smart fileUpload component
            form.sections.forEach((section) => {
                if (section.entries) {
                    section.entries.forEach((entry) => {
                        if (entry.smartUpload) {
                            entry.template = {};
                        }
                    });
                }
            });
        } else {
            form = {
                name: name,
                sections: [
                    {
                        name: 'New Section',
                        entries: [],
                    },
                ],
            };
        }
        dispatch(formSave({ form, callBack }));
    },
});

const insertIntoSectionList = (array, index, item) => {
    const updatedArray = [...array];
    updatedArray.splice(index, 0, item);
    return updatedArray;
};

const editSectionName = (state, name) => {
    const { form, activeSection } = state;

    const sections = [...form.sections];
    const renamedSection = {
        ...sections[activeSection],
        name,
    };
    sections.splice(activeSection, 1, renamedSection);

    return { sections };
};

const addLinkedFormSection = (state, formData) => {
    const { form, activeSection } = state;

    const section = { form: formData };
    const sections = insertIntoSectionList(form.sections, activeSection + 1, section);
    let updatedActiveSection;
    if (sections.every((section) => section.form)) {
        updatedActiveSection = activeSection + 1;
    }

    return { sections, updatedActiveSection };
};

const calculateActiveSection = (sections, activeSection, deletedSection) => {
    if (
        activeSection >= sections.length ||
        activeSection > deletedSection ||
        (activeSection === deletedSection && activeSection === sections.length) ||
        sections[activeSection].form
    ) {
        return stepBackActiveSection(sections, activeSection);
    } else if (sections.every((section) => section.form)) {
        return activeSection;
    } else {
        return activeSection;
    }
};

const deleteSection = (state, sectionIndex) => {
    const { form, activeSection } = state;
    const sections = [...form.sections];
    sections.splice(sectionIndex, 1);
    const updatedActiveSection = calculateActiveSection(sections, activeSection, sectionIndex);
    return { sections, updatedActiveSection };
};

const addSection = (state) => {
    const { form, activeSection } = state;

    const newSection = {
        name: 'New Section',
        entries: [],
    };
    const newSectionIndex = activeSection + 1;
    const sections = insertIntoSectionList(form.sections, newSectionIndex, newSection);

    return { sections, updatedActiveSection: activeSection + 1 };
};

const updateSections = createLogic({
    type: formUpdateSections,
    latest: true,
    warnTimeout: 0,
    processOptions: {
        dispatchMultiple: true,
    },
    process({ getState, action }, dispatch) {
        const state = getState().formBuilder;
        let updates;

        switch (action.payload.action) {
            case 'add':
                updates = addSection(state);
                break;
            case 'delete':
                updates = deleteSection(state, action.payload.index);
                break;
            case 'import':
                updates = addLinkedFormSection(state, action.payload.data);
                break;
            case 'json':
                // when DataEditor updates, uses index argument for form data
                updates = {
                    sections: action.payload.index.sections,
                    updatedActiveSection: calculateActiveSection(action.payload.index.sections, state.activeSection)
                };
                break;
            default:
                updates = editSectionName(state, action.payload.name);
                break;
        }

        const { sections, updatedActiveSection } = updates;
        const form = { ...state.form, sections };

        if (Number.isFinite(updatedActiveSection)) {
            dispatch(formChangeSection({ activeSection: updatedActiveSection, form }));
        } else {
            dispatch(formUpdated(form));
        }
    },
});

const reorderSections = createLogic({
    type: formReorderSections,
    latest: true,
    warnTimeout: 0,
    process({ getState, action }, dispatch) {
        const form = getState().formBuilder.form;
        const newForm = { ...form, sections: action.payload };
        dispatch(formUpdated(newForm));
    },
});

const renameMultipleField = createLogic({
    type: formRenameMultipleField,
    latest: true,
    warnTimeout: 0,
    processOptions: {
        dispatchMultiple: true,
    },
    process({ getState, action }, dispatch) {
        const form = getState().formBuilder.form;
        const section = form.sections[getState().formBuilder.activeSection];
        const index = action.payload.index;
        const newEntries = [
            ...section.entries.slice(0, index),
            {
                ...section.entries[index],
                label: action.payload.label,
            },
            ...section.entries.slice(index + 1),
        ];
        let newSections = [];
        newSections = [
            ...form.sections.slice(0, getState().formBuilder.activeSection),
            {
                ...form.sections[getState().formBuilder.activeSection],
                entries: newEntries,
            },
            ...form.sections.slice(getState().formBuilder.activeSection + 1),
        ];
        const newForm = { ...form, sections: newSections };
        dispatch(formUpdated(newForm));
    },
});
const updateVisibilityAndValidation = createLogic({
    type: formUpdateVisibilityAndValidation,
    latest: true,
    warnTimeout: 0,
    process({ getState, action }, dispatch) {
        const form = action.payload.newForm;
        const index = action.payload.index;
        const currentSection = getState().formBuilder.form.sections[getState().formBuilder.activeSection];
        let updatedForm;
        let prevFieldName;
        // do not set prevFieldName when adding a field
        if (index === undefined) {
            prevFieldName = action.payload.updatedEntry.field;
        } else if (
            index !== currentSection.entries.length &&
            currentSection.entries[index].field !== action.payload.updatedEntry.field
        ) {
            prevFieldName = currentSection.entries[index].field;
        }
        if (prevFieldName !== undefined) {
            updatedForm = {
                ...form,
                sections: form.sections.map((section, index) => {
                    if (!section.entries) {
                        return section;
                    }

                    if (action.payload.index !== undefined) {
                        return {
                            ...section,
                            entries: updateVisibilityAndValidationOnNameChange(
                                section.entries,
                                action.payload.updatedEntry.field,
                                prevFieldName,
                            ),
                        };
                    } else {
                        return {
                            ...section,
                            entries: updateVisibilityAndValidationOnDelete(
                                section.entries,
                                prevFieldName,
                                action.payload.referenceIndexes[index],
                            ),
                        };
                    }
                }),
            };
        }
        dispatch(formUpdated(updatedForm ?? form));
    },
});
const updateEntries = createLogic({
    type: formUpdateEntries,
    latest: true,
    warnTimeout: 0,
    process({ getState, action }, dispatch) {
        const form = getState().formBuilder.form;
        const currentSection = form.sections[getState().formBuilder.activeSection];
        const index = action.payload.index;
        const updatedEntry = action.payload.entry;
        const referenceIndexes = action.payload.referenceIndexes;
        let newEntries = [];
        if (action.payload.index !== undefined) {
            newEntries = [
                ...currentSection.entries.slice(0, index),
                updatedEntry,
                ...currentSection.entries.slice(index + 1),
            ];
        } else {
            newEntries = currentSection.entries.filter((entry) => entry !== action.payload.entry);
        }
        const newForm = {
            ...form,
            sections: [
                ...form.sections.slice(0, getState().formBuilder.activeSection),
                { ...currentSection, entries: newEntries },
                ...form.sections.slice(getState().formBuilder.activeSection + 1),
            ],
        };
        dispatch(formUpdateVisibilityAndValidation({ index, updatedEntry, newForm, referenceIndexes }));
    },
});

const hasSearchProperties = (searchForm) => {
    if (
        searchForm?.fields.length ||
        searchForm?.addRecordText ||
        searchForm?.api?.url ||
        (searchForm?.api?.resultMappings && searchForm?.api?.resultMappings.length > 0)
    ) {
        return true;
    }
    return false;
};

const updateMultipleEntries = createLogic({
    type: formUpdateMultipleEntries,
    latest: true,
    warnTimeout: 0,
    process({ getState, action }, dispatch) {
        const form = getState().formBuilder.form;
        const section = form.sections[getState().formBuilder.activeSection];
        const fieldIndex = action.payload.fieldIndex;
        const newFields = action.payload.fields;
        const searchForm = action.payload.searchForm;

        let newMultipleField = {
            ...section.entries[fieldIndex],
            fields: newFields,
        };

        if (searchForm) {
            if (hasSearchProperties(searchForm)) {
                newMultipleField = {
                    ...newMultipleField,
                    searchForm: {
                        ...searchForm,
                    },
                };
            } else if (searchForm.fields.length === 0) {
                const { searchForm, ...propsWithoutSearchForm } = newMultipleField;
                newMultipleField = {
                    ...propsWithoutSearchForm,
                };
            }
        }

        const newEntries = [
            ...section.entries.slice(0, fieldIndex),
            newMultipleField,
            ...section.entries.slice(fieldIndex + 1),
        ];
        const newForm = {
            ...form,
            sections: [
                ...form.sections.slice(0, getState().formBuilder.activeSection),
                { ...section, entries: newEntries },
                ...form.sections.slice(getState().formBuilder.activeSection + 1),
            ],
        };
        dispatch(formUpdated(newForm));
    },
});
const setFormEntries = createLogic({
    type: formSetEntries,
    latest: true,
    warnTimeout: 0,
    process({ getState, action }, dispatch) {
        const form = getState().formBuilder.form;
        const newSections = [
            ...form.sections.slice(0, getState().formBuilder.activeSection),
            {
                ...form.sections[getState().formBuilder.activeSection],
                entries: action.payload,
            },
            ...form.sections.slice(getState().formBuilder.activeSection + 1),
        ];
        const newForm = { ...form, sections: newSections };

        dispatch(formUpdated(newForm));
    },
});
const updateName = createLogic({
    type: formUpdateName,
    latest: true,
    warnTimeout: 0,
    process({ getState, action }, dispatch) {
        const form = getState().formBuilder.form;
        const newForm = { ...form, name: action.payload };
        dispatch(formUpdated(newForm));
    },
});

const saveForm = createLogic({
    type: formSave,
    latest: true,
    async process({ getState, action }, dispatch, done) {
        const existingFormDefinition = action.payload.form ? action.payload.form : getState().formBuilder.form;
        const form = {
            client: 'OnlineServices',
            ...existingFormDefinition,
            version: 1,
        };

        const authContext = getState().authentication;
        const token = await authContext.getAccessToken(authContext.authorizationScopes.forms);

        saveFormData(form, token)
            .then((result) => {
                if (result.data && result.data.id) {
                    if (!form.id) {
                        form.id = result.data.id;
                    }
                }
                dispatch(formSaved(form));
                if (action.payload.callBack) {
                    action.payload.callBack('success');
                }
            })
            .catch(() => {
                if (action.payload.callBack) {
                    action.payload.callBack('error');
                }
            })
            .finally(done);
    },
});

const discardFormChanges = createLogic({
    type: formDiscardChanges,
    latest: true,
    warnTimeout: 0,
    async process({ getState }, dispatch, done) {
        const authContext = getState().authentication;
        const token = await authContext.getAccessToken(authContext.authorizationScopes.forms);
        const form = {
            ...getState().formBuilder.lastSavedForm,
            version: 1,
        };
        const updatedForm = await updateFormAttachments(form, token);
        dispatch(formSaved(updatedForm));
        dispatch(push('/'));
        done();
    },
});

const saveFormWithRedirection = createLogic({
    type: formSaveAndRedirect,
    latest: true,
    async process({ getState, action }, dispatch, done) {
        const form = {
            client: 'OnlineServices',
            ...getState().formBuilder.form,
            version: 1,
        };

        const authContext = getState().authentication;
        const token = await authContext.getAccessToken(authContext.authorizationScopes.forms);

        saveFormData(form, token)
            .then((result) => {
                if (result.data && result.data.id) {
                    if (!form.id) {
                        form.id = result.data.id;
                    }
                }
                dispatch(formSaved(form));
                action.payload.callBack('success');
                dispatch(push('/'));
            })
            .catch(() => {
                action.payload.callBack('error');
            })
            .finally(done);
    },
});

const deleteForm = createLogic({
    type: formDelete,
    latest: true,
    async process({ getState, action }, dispatch, done) {
        const form = action.payload;
        const authContext = getState().authentication;
        const token = await authContext.getAccessToken(authContext.authorizationScopes.forms);

        apiDelete(`/forms/${form.id}`, token)
            .then(() => {
                dispatch(formsLoadExisting());
            })
            .catch(() => {})
            .finally(done);
    },
});

async function saveFormData(form, token) {
    const updatedForm = await updateFormAttachments(form, token);
    return apiPost('/forms', updatedForm, token);
}

async function updateFormAttachments(form, token) {
    if (form.id) {
        const formAttachments = await filterOutOrphanFiles(form, token);
        form.attachments = formAttachments;
    }
    return form;
}

async function filterOutOrphanFiles(form, token) {
    const formAttachments = [];

    const currentAttachmentIds = [];
    form.sections.forEach((section) => {
        if (section.entries) {
            section.entries.forEach((entry) => {
                if (entry.typeId === 'fileUpload' && entry.template) {
                    currentAttachmentIds.push(entry.template.id);
                }
            });
        }
    });

    const response = await apiGet(`/forms/${form.id}/attachments`, token);
    const storedFormAttachments = response.data;
    if (storedFormAttachments) {
        storedFormAttachments.forEach(async (storedAttachment) => {
            if (!currentAttachmentIds.includes(storedAttachment.id)) {
                const urlEncodedFileId = encodeURIComponent(storedAttachment.id);
                const doubleUrlEncodedFileId = encodeURIComponent(urlEncodedFileId);
                await apiDelete(
                    `/forms/${form.id}/attachments/${doubleUrlEncodedFileId}`,
                    token,
                );
            } else {
                formAttachments.push(storedAttachment);
            }
        });
    }

    return formAttachments;
}

export default [
    loadFormBuilder,
    startFormBuilder,
    updateSections,
    reorderSections,
    renameMultipleField,
    updateEntries,
    updateMultipleEntries,
    saveForm,
    updateName,
    setFormEntries,
    saveFormWithRedirection,
    deleteForm,
    discardFormChanges,
    updateVisibilityAndValidation,
];
